import { createAsyncThunk } from '@reduxjs/toolkit'

import { get, has } from 'lodash'

import { API } from '@/api'
import { GetSemiAutoLabelingReportResponse, LabelingNodeInfo, LabelingNodeItem, UserShortcut } from '@/api/LabelingNode/types'

import { ThunkConfig } from '@/stores/slices/Labeling/types'

import { DEFAULT_SHORTCUTS } from '@/pages/Labeling/components/ShortcutDrawer/hotKeys'

type LoadLabelingDataResult = {
  labelingNodeId: string
  labelingNodeInfo: LabelingNodeInfo
  userMeta: { lastIndex: number }
  userShortcuts: UserShortcut[]
  promptImages: LabelingNodeItem[]
  userId: string;
  semiAutoLabelingReportList: GetSemiAutoLabelingReportResponse[]
};

export const loadLabelingData = createAsyncThunk<LoadLabelingDataResult, string, ThunkConfig>(
  'labeling/load-labeling-data',
  async (labelingNodeId: string, thunkAPI) => {
    const { user } = thunkAPI.getState().user
    /* eslint-disable prefer-const */
    let [
      { lastIndex },
      labelingNodeInfo,
      promptImages,
      userShortcuts,
      semiAutoLabelingReportList,
    ] = await Promise.all([
      API().Labeling.getUserMeta(labelingNodeId),
      API().Labeling.getNodeDetails(labelingNodeId),
      API().Labeling.getSelectedLabelAssistantItems(labelingNodeId),
      API().Labeling.getUserShortcuts(),
      API().Labeling.getSemiAutoLabelingReportList(labelingNodeId),
    ])

    // 저장된 단축키 정보가 없는 유저일 경우, 디폴트 단축키 강제 저장
    if (has(userShortcuts, 'data') && get(userShortcuts, 'data') === undefined) {
      userShortcuts = await API().Labeling.updateUserShortcuts({ shortcutKeySettings: DEFAULT_SHORTCUTS })
    }

    return {
      labelingNodeId,
      labelingNodeInfo,
      userMeta: { lastIndex },
      userShortcuts: userShortcuts?.shortCutKeySettings,
      promptImages: promptImages.data,
      userId: user._id,
      semiAutoLabelingReportList: semiAutoLabelingReportList?.data ?? [],
    }
  }
)

export const loadAllLabelingItems = createAsyncThunk<LabelingNodeItem[], { labelingNodeId: string, locationFileId?: string }, ThunkConfig>(
  'labeling/load-all-labeling-items',
  async ({ labelingNodeId }, thunkAPI) => {
    const { numOfLabelingData: filesLength, hasSelectedForClustering } = thunkAPI.getState().labeling

    const MAX_FILES_PER_PAGE = 50
    const itemsPerPage = Math.min(filesLength, MAX_FILES_PER_PAGE)
    const MAX_PAGE = Math.ceil(filesLength / itemsPerPage)

    const result = await Promise.all(
      Array.from({ length: MAX_PAGE }, (_, page) => API().Labeling.getLabelingItems(labelingNodeId, {
        page,
        itemsPerPage,
        isSelectedForClustering: hasSelectedForClustering,
      }))
    )

    return result.flatMap((f) => f.list)
  }
)

export const initializeClasses = createAsyncThunk<any, string, ThunkConfig>(
  'labeling/initialize-classes',
  async (labelingNodeId: string) => {
    const { data: classes } = await API().Labeling.getUserClasses(labelingNodeId)
    return classes
  }
)
